import type { Plan, PlanSeed, UnitsPreference } from "@heraion/shared";
import { ProgramComposer, type CatalogItem } from "@heraion/ui";

import { apiFetch } from "./api";
import { confirmViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { ExercisePickerMount } from "./ExercisePickerMount";

/** What the Home surface's plan editor is open on: a plan to edit, or a new
 *  one, blank or made from a session (#368). `key` makes every new one a
 *  fresh editor, never one already open on another plan. */
export type PlanEditorTarget =
  | { kind: "edit"; plan: Plan }
  | { kind: "new"; seed: PlanSeed | null; key: number };

let newPlans = 0;
export const newPlanTarget = (seed: PlanSeed | null): PlanEditorTarget => {
  newPlans += 1;
  return { kind: "new", seed, key: newPlans };
};

// The plan composer (single mode) on the Home surface, split out of
// PlanScreen.tsx (file-size cap, #368). A separate full-screen surface,
// same as the program composer there.
export function PlanComposerMount({
  target,
  dark,
  catalog,
  units,
  callerSub,
  athleteRoster,
  draftScope,
  onClose,
}: {
  target: PlanEditorTarget;
  dark: boolean;
  catalog: CatalogItem[];
  units: UnitsPreference;
  callerSub?: string;
  athleteRoster?: ReadonlyArray<string>;
  draftScope: string | null;
  /** Saved or left. Nothing to refresh: the plans list lives in the hub's
   *  Library, which refetches on open. */
  onClose: () => void;
}) {
  return (
    <ProgramComposer
      key={target.kind === "edit" ? target.plan.id : `new-plan-${target.key}`}
      mode="single"
      apiFetch={apiFetch}
      durableWrite={durableWrite}
      dark={dark}
      plan={target.kind === "edit" ? target.plan : undefined}
      planSeed={target.kind === "new" ? (target.seed ?? undefined) : undefined}
      catalog={catalog}
      Picker={ExercisePickerMount}
      unitsPreference={units}
      confirm={confirmViaWindow}
      callerSub={callerSub}
      athleteRoster={athleteRoster}
      keepOpen
      draftScope={draftScope}
      draftOwner={callerSub ?? null}
      onSaved={onClose}
      onCancel={onClose}
    />
  );
}
